import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, className = "", as = "div", ...rest }) => {
  const reduce = useReducedMotion();
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px -12% 0px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  return (
    <span className={`block overflow-hidden pb-[0.08em] ${className}`}>
      <motion.span
        className="block whitespace-nowrap will-change-transform"
        initial={reduce ? { opacity: 0 } : { y: "115%" }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const ParallaxImage = ({ src, alt, className = "", imgClassName = "", testId }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1.03]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        data-testid={testId}
        className={`h-full w-full object-cover will-change-transform ${imgClassName}`}
        style={reduce ? undefined : { y, scale }}
      />
    </div>
  );
};

export const ChapterNumber = ({ children, className = "" }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none select-none absolute font-serif italic leading-none text-secondary/[0.08] ${className}`}
    style={{ fontSize: "clamp(9rem, 22vw, 20rem)" }}
  >
    {children}
  </span>
);

export const Spotlight = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute rounded-full opacity-[0.14] blur-3xl ${className}`}
    style={{ background: "radial-gradient(circle, hsl(211 61% 43%) 0%, transparent 70%)" }}
  />
);

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};
